import { Button, Group, Stack, Stepper } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconSend } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { isBatchOfInvoices, openAmlCase } from './open-aml-case';
import { useAmlForm } from 'shared/components/InvoiceActionsDropdown/AmlModal/hooks/useAmlForm';
import { useAmlStepper } from 'shared/components/InvoiceActionsDropdown/AmlModal/hooks/useAmlStepper';
import { useCreateAmlPdf } from 'shared/hooks/use-create-aml-pdf';
import { useSendToThirdParty } from 'shared/hooks/use-send-to-third-party';
import { useGetInvoiceState } from 'shared/utils/selectors';
import type { Id } from 'types';
import { AdditionalInfoStep } from './components/AdditionalInfoStep';
import { CompanyStep } from './components/CompanyStep';
import { IdentityStep } from './components/IdentityStep';
import { RelatedPersonsStep } from './components/RelatedPersonsStep';
import { RepresentativeStep } from './components/RepresentativeStep';
import { AmlFormProvider, useAmlFormContext } from './types/types';

type AmlModalProps = {
  invoiceIds: Array<Id>;
  thirdPartyCompanyId: Id;
  additionalInvoiceIds: Array<Id>;
  onSubmit: (values: Record<string, unknown>) => void;
  onSendSuccess?: () => void;
};

function AmlModalContent({
  invoiceIds,
  thirdPartyCompanyId,
  additionalInvoiceIds,
  onSubmit,
  onSendSuccess,
}: AmlModalProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const form = useAmlFormContext();

  const { sendToThirdParty, isSendToThirdPartyLoading } = useSendToThirdParty();
  const { createAmlPdf, isCreateAmlPdfLoading } = useCreateAmlPdf();
  const { isLoading: isBatchActionLoading } = useGetInvoiceState();

  const {
    activeStep,
    idFront,
    idBack,
    setIdFront,
    setIdBack,
    isStepComplete,
    goNextStep,
    goPrevStep,
    TOTAL_STEPS,
  } = useAmlStepper();

  const isBatch = isBatchOfInvoices(invoiceIds);

  const amlData = {
    ...form.values,
    pep: form.values.pep === 'true',
    idFront,
    idBack,
  };

  const openCase = () =>
    openAmlCase({
      amlData,
      invoiceIds,
      thirdPartyCompanyId,
      additionalInvoiceIds,
      createAmlPdf,
      sendToThirdParty,
      onSubmit,
      onSendSuccess,
      navigate: history.push,
      closeModals: modals.closeAll,
    });
  const isLoading =
    isCreateAmlPdfLoading || (isBatch ? isBatchActionLoading : isSendToThirdPartyLoading);

  return (
    <Stack px="md" gap="md">
      <Stepper active={activeStep} size="sm" mb="md">
        <Stepper.Step label={t(i18nKeys.AML_MODAL.STEPS.COMPANY)}>
          <CompanyStep />
        </Stepper.Step>

        <Stepper.Step label={t(i18nKeys.AML_MODAL.STEPS.RELATED_PERSONS)}>
          <RelatedPersonsStep />
        </Stepper.Step>

        <Stepper.Step label={t(i18nKeys.AML_MODAL.STEPS.REPRESENTATIVE)}>
          <RepresentativeStep />
        </Stepper.Step>

        <Stepper.Step label={t(i18nKeys.AML_MODAL.STEPS.ADDITIONAL_INFO)}>
          <AdditionalInfoStep />
        </Stepper.Step>

        <Stepper.Step label={t(i18nKeys.AML_MODAL.STEPS.IDENTITY)}>
          <IdentityStep
            idFront={idFront}
            idBack={idBack}
            setIdFront={setIdFront}
            setIdBack={setIdBack}
          />
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button variant="outline" onClick={goPrevStep} disabled={activeStep === 0}>
          {t(i18nKeys.AML_MODAL.ACTIONS.PREVIOUS)}
        </Button>
        {activeStep < TOTAL_STEPS - 1 ? (
          <Button onClick={goNextStep} disabled={!isStepComplete(activeStep)}>
            {t(i18nKeys.AML_MODAL.ACTIONS.NEXT)}
          </Button>
        ) : (
          <Button
            leftSection={<IconSend size={16} />}
            onClick={openCase}
            disabled={!isStepComplete(TOTAL_STEPS - 1)}
            loading={isLoading}
          >
            {t(i18nKeys.AML_MODAL.ACTIONS.SEND)}
          </Button>
        )}
      </Group>
    </Stack>
  );
}

export function AmlModal(props: AmlModalProps) {
  const form = useAmlForm();

  return (
    <AmlFormProvider form={form}>
      <AmlModalContent {...props} />
    </AmlFormProvider>
  );
}
